import * as signalR from '@microsoft/signalr';

class NotificationHub {
  constructor() {
    this.connection = null;
    this.listeners = {};
    this.isInitialized = false;
    this.lastError = null;
    this.isConnecting = false;
    this.currentCompanyId = null;
  }

  getHubUrl() {
    const apiUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
    return `${apiUrl}/hubs/notifications`;
  }

  registerServerEvents(connection, includeWorkflowEvents = false) {
    connection.on('NewBooking', (data) => this._emit('NewBooking', data));
    connection.on('NewOrder', (data) => this._emit('NewOrder', data));
    connection.on('NewBookingRequest', (data) => this._emit('NewBookingRequest', data));
    connection.on('BookingRequestStatusChanged', (data) => this._emit('BookingRequestStatusChanged', data));
    connection.on('PaymentConfirmed', (data) => this._emit('PaymentConfirmed', data));
    connection.on('CustomerChatMessage', (data) => this._emit('CustomerChatMessage', data));
    connection.on('CompanyChatMessage', (data) => this._emit('CompanyChatMessage', data));
    connection.on('BookingPriceFinalized', (data) => this._emit('BookingPriceFinalized', data));
    connection.on('BookingRequestPriceFinalized', (data) => this._emit('BookingRequestPriceFinalized', data));

    if (includeWorkflowEvents) {
      [
        'BookingRequestWorkflowStarted',
        'WorkflowComment',
        'WorkflowStageAdvanced',
        'BookingRequestRejected',
        'BookingRequestFullyApproved',
      ].forEach((eventName) => {
        connection.on(eventName, (data) => this._emit(eventName, data));
      });
    }
  }

  async connect(companyId) {
    if (!companyId || this.isConnecting) return;

    if (
      this.isInitialized &&
      this.currentCompanyId === companyId &&
      this.connection?.state === signalR.HubConnectionState.Connected
    ) {
      return;
    }

    if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
      await this.connection.stop();
    }

    this.isConnecting = true;
    this.currentCompanyId = companyId;
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(this.getHubUrl(), {
        accessTokenFactory: () => localStorage.getItem('accessToken') || '',
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .build();

    this.connection.onclose(() => {
      this.isInitialized = false;
    });

    this.connection.onreconnecting(() => {
      this.isInitialized = false;
    });

    this.connection.onreconnected(async () => {
      try {
        if (this.currentCompanyId) {
          await this.connection.invoke('JoinCompanyGroup', this.currentCompanyId);
        }
        this.isInitialized = true;
        this.lastError = null;
        this._emit('ConnectionRestored', { companyId: this.currentCompanyId });
      } catch (error) {
        this.lastError = error.message || error.toString();
        console.error('Failed to rejoin notification company group:', error);
      }
    });

    this.registerServerEvents(this.connection, true);

    try {
      await this.connection.start();
      await this.connection.invoke('JoinCompanyGroup', companyId);
      this.isInitialized = true;
      this.isConnecting = false;
      this.lastError = null;
    } catch (error) {
      this.isInitialized = false;
      this.isConnecting = false;
      this.lastError = error.message || error.toString();
      await this.connection?.stop().catch(() => {});
      throw error;
    }
  }

  async joinPaymentGroup(referenceCode) {
    if (!this.connection) await this.connectAsGuest();
    await this.connection.invoke('JoinPaymentGroup', referenceCode);
  }

  async leavePaymentGroup(referenceCode) {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('LeavePaymentGroup', referenceCode);
    }
  }

  async connectAsGuest() {
    if (
      this.isInitialized &&
      this.connection?.state === signalR.HubConnectionState.Connected
    ) return;

    if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
      await this.connection.stop();
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(this.getHubUrl(), { withCredentials: true })
      .withAutomaticReconnect()
      .build();

    this.registerServerEvents(this.connection, false);
    await this.connection.start();
    this.isInitialized = true;
    this.currentCompanyId = null;
  }

  async disconnect(companyId) {
    if (!this.connection) return;

    try {
      if (
        companyId &&
        this.connection.state === signalR.HubConnectionState.Connected &&
        this.currentCompanyId === companyId
      ) {
        await this.connection.invoke('LeaveCompanyGroup', companyId);
      }
    } catch (error) {
      console.warn('Failed to leave notification company group cleanly:', error);
    } finally {
      await this.connection.stop().catch(() => {});
      this.connection = null;
      this.isInitialized = false;
      this.isConnecting = false;
      this.currentCompanyId = null;
    }
  }

  on(event, callback) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter((listener) => listener !== callback);
  }

  _emit(event, data) {
    [...(this.listeners[event] || [])].forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Notification listener failed for ${event}:`, error);
      }
    });
  }

  getConnectionStatus() {
    const statusMap = {
      [signalR.HubConnectionState.Connected]: 'Connected',
      [signalR.HubConnectionState.Connecting]: 'Connecting',
      [signalR.HubConnectionState.Reconnecting]: 'Reconnecting',
      [signalR.HubConnectionState.Disconnected]: 'Disconnected',
    };

    return {
      status: this.connection ? (statusMap[this.connection.state] || 'Unknown') : 'Not initialized',
      isInitialized: this.isInitialized,
      isConnecting: this.isConnecting,
      currentCompanyId: this.currentCompanyId,
      lastError: this.lastError,
      hasAccessToken: Boolean(localStorage.getItem('accessToken')),
      apiUrl: import.meta.env.VITE_API_BASE_URL || 'Not configured',
    };
  }

  testNotification(type = 'booking') {
    const testData = type === 'booking' ? {
      BookingId: '123e4567-e89b-12d3-a456-426614174000',
      ReferenceCode: 'TEST-BK-123',
      ClientName: 'Test Client',
      ServiceName: 'Test Service',
      Date: '2026-04-01',
      Time: '10:00 AM',
    } : {
      OrderId: '123e4567-e89b-12d3-a456-426614174001',
      ReferenceCode: 'TEST-ORD-456',
      ClientName: 'Test Client',
      TotalAmount: 75.50,
      ItemCount: 3,
    };

    this._emit(type === 'booking' ? 'NewBooking' : 'NewOrder', testData);
  }
}

export default new NotificationHub();
